// Single home for resolving Document Center status labels + colors.
//
// The Document Center aggregates four document types, each with its own status
// enum AND its own existing i18n vocabulary. Rather than duplicate those labels,
// we delegate to each type's namespace so the Polish/English wording (incl.
// gender, e.g. invoice ISSUED -> "Wystawiona" vs WZ ISSUED -> "Wystawiony")
// stays owned by the source module.
//
//   PZ               -> pz:status.{lower}
//   WZ               -> wz:status.{lower}
//   SALES_INVOICE    -> invoices:status.{lower}
//   PURCHASE_INVOICE -> orders:supplierOrders.status.{UPPER}
//   PURCHASE_INVOICE (invoice-workflow row) -> invoices:workflow.page.status.{lower}
//
// Unknown/missing keys fall back to the raw enum value so new backend statuses
// never render blank.
import type { TFunction } from 'i18next';
import type { DocumentCenterType } from './api/documentCenterApi';

// i18n key builder per document type. Returns the namespaced key whose label
// lives in the source module, or null when the type is unknown.
const statusKeyByType = (type: DocumentCenterType, status: string, workflow = false): string | null => {
  if (workflow && type === 'PURCHASE_INVOICE') {
    return `invoices:workflow.page.status.${status.toLowerCase()}`;
  }
  switch (type) {
    case 'PZ':
      return `pz:status.${status.toLowerCase()}`;
    case 'WZ':
      return `wz:status.${status.toLowerCase()}`;
    case 'SALES_INVOICE':
      return `invoices:status.${status.toLowerCase()}`;
    case 'PURCHASE_INVOICE':
      return `orders:supplierOrders.status.${status.toUpperCase()}`;
    default:
      return null;
  }
};

// Resolve the human-facing status label for a document row. Unknown backend
// enums are never displayed as technical values in the operator UI.
export const getDocumentCenterStatusLabel = (
  t: TFunction,
  type: DocumentCenterType,
  status?: string | null,
  workflow = false
): string => {
  if (!status) return t('common:unknownStatus');
  const key = statusKeyByType(type, status, workflow);
  if (!key) return t('common:unknownStatus');
  // i18next returns the key itself when missing -> treat that as "no translation".
  const label = t(key, { defaultValue: '' });
  return label || t('common:unknownStatus');
};

// Semantic Ant Design Tag colors keyed by normalized (uppercase) status. Shared
// across all four document types so the column reads consistently regardless of
// source. Invariant: when any source enum (PZ/WZ/invoice/supplier-order) gains a
// status, add it here too — otherwise the tag falls back silently to 'default'
// (grey). The fallback is safe (never blank), so a missing entry is cosmetic.
const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'default',
  SENT: 'processing',
  CONFIRMED: 'blue',
  ISSUED: 'processing',
  IN_PRODUCTION: 'gold',
  PARTIALLY_READY: 'cyan',
  READY: 'cyan',
  RELEASED: 'cyan',
  SHIPPED: 'purple',
  PARTIALLY_RECEIVED: 'gold',
  RECEIVED: 'green',
  ON_HOLD: 'magenta',
  PAID: 'green',
  CANCELLED: 'red',
  APPROVED: 'green',
};

export const getDocumentCenterStatusColor = (status?: string | null): string => {
  if (!status) return 'default';
  return STATUS_COLORS[status.toUpperCase()] || 'default';
};
